import { useState } from 'react';
import {
  Check,
  Copy,
  Code,
  Terminal,
  FileCode,
  Globe,
  Lock,
  Zap,
  Book,
  ExternalLink,
} from 'lucide-react';
import { copyToClipboard } from '@/lib/utils';

interface IntegrationGuideProps {
  project: {
    id: string;
    name: string;
    slug: string;
    apiKey?: string;
    apiKeyPrefix?: string;
    domains?: string[];
  };
}

export default function IntegrationGuide({ project }: IntegrationGuideProps) {
  const [activeTab, setActiveTab] = useState<'quickstart' | 'sdk' | 'rest' | 'examples'>(
    'quickstart'
  );
  const [copiedItems, setCopiedItems] = useState<Record<string, boolean>>({});

  const handleCopy = async (text: string, key: string) => {
    await copyToClipboard(text);
    setCopiedItems({ ...copiedItems, [key]: true });
    setTimeout(() => setCopiedItems({ ...copiedItems, [key]: false }), 2000);
  };

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';
  const apiKey = project.apiKey || `${project.apiKeyPrefix}••••••••••••••••••••••`;

  const CodeBlock = ({
    code,
    language,
    copyKey,
  }: {
    code: string;
    language: string;
    copyKey: string;
  }) => (
    <div className="relative">
      <div className="flex items-center justify-between rounded-t-lg bg-zinc-800 px-4 py-2">
        <span className="font-mono text-xs text-zinc-400">{language}</span>
        <button
          onClick={() => handleCopy(code, copyKey)}
          className="flex items-center gap-1 text-xs text-zinc-400 transition-colors hover:text-white"
        >
          {copiedItems[copyKey] ? (
            <>
              <Check className="h-3 w-3 text-green-400" />
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto rounded-b-lg bg-zinc-900 p-4 text-sm">
        <code className="whitespace-pre font-mono text-zinc-100">{code}</code>
      </pre>
    </div>
  );

  const tabs = [
    { id: 'quickstart', label: 'Quick Start', icon: Zap },
    { id: 'sdk', label: 'SDK Setup', icon: Code },
    { id: 'rest', label: 'REST API', icon: Globe },
    { id: 'examples', label: 'Examples', icon: Book },
  ];

  const envVars = `# Add to your .env file
VITE_AAPASTECH_API_URL=${apiUrl}
VITE_AAPASTECH_API_KEY=${apiKey}
VITE_AAPASTECH_PROJECT_ID=${project.id}`;

  const sdkInit = `// lib/aapastech.ts
import { AapasTech } from '@aapastech/sdk';

export const aapastech = new AapasTech({
  apiUrl: import.meta.env.VITE_AAPASTECH_API_URL,
  apiKey: import.meta.env.VITE_AAPASTECH_API_KEY,
});`;

  const newsletterExample = `// Subscribe to newsletter
const handleSubscribe = async (email: string) => {
  const response = await fetch('${apiUrl}/public/newsletter/subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': '${apiKey}',
    },
    body: JSON.stringify({
      email,
      source: 'website',
    }),
  });

  const data = await response.json();
  if (data.success) {
    console.log('Subscribed successfully!');
  }
};`;

  const contactExample = `// Submit contact form
const handleContact = async (formData: {
  name: string;
  email: string;
  subject: string;
  message: string;
}) => {
  const response = await fetch('${apiUrl}/public/contact', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': '${apiKey}',
    },
    body: JSON.stringify(formData),
  });

  const data = await response.json();
  return data;
};`;

  const reactFormExample = `// React Newsletter Form Component
import { useState } from 'react';

export function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('${apiUrl}/public/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '${apiKey}',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        required
        className="flex-1 px-4 py-2 border rounded-lg"
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
      </button>
      {status === 'success' && <span className="text-green-600">✓ Subscribed!</span>}
      {status === 'error' && <span className="text-red-600">Failed to subscribe</span>}
    </form>
  );
}`;

  const curlExamples = `# Test newsletter subscription
curl -X POST ${apiUrl}/public/newsletter/subscribe \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{"email": "test@example.com"}'

# Test contact form
curl -X POST ${apiUrl}/public/contact \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{"name": "John", "email": "john@example.com", "message": "Hello!"}'`;

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Quick Start Tab */}
      {activeTab === 'quickstart' && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="bg-card rounded-xl border p-4">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FileCode className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="mb-1 font-medium">1. Add Environment Variables</h3>
              <p className="text-muted-foreground text-sm">Configure your API credentials</p>
            </div>
            <div className="bg-card rounded-xl border p-4">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Terminal className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="mb-1 font-medium">2. Install SDK (Optional)</h3>
              <p className="text-muted-foreground text-sm">Or use REST API directly</p>
            </div>
            <div className="bg-card rounded-xl border p-4">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="mb-1 font-medium">3. Start Integrating</h3>
              <p className="text-muted-foreground text-sm">Use newsletter, contact & more</p>
            </div>
          </div>

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <FileCode className="h-4 w-4" />
              Environment Variables
            </h3>
            <CodeBlock code={envVars} language=".env" copyKey="quickstart-env" />
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Security Note
                </p>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Your API key is tied to specific domains for security. Make sure your domain is
                  configured in project settings. The public API endpoints are rate-limited and
                  require valid API key headers.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SDK Setup Tab */}
      {activeTab === 'sdk' && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Terminal className="h-4 w-4" />
              Install the SDK
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <CodeBlock
                code="npm install @aapastech/sdk"
                language="bash (npm)"
                copyKey="sdk-npm"
              />
              <CodeBlock code="yarn add @aapastech/sdk" language="bash (yarn)" copyKey="sdk-yarn" />
            </div>
          </div>

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Code className="h-4 w-4" />
              Initialize the SDK
            </h3>
            <CodeBlock code={sdkInit} language="typescript" copyKey="sdk-init" />
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Note:</strong> The SDK is optional. You can use the REST API directly with
              fetch or axios. See the REST API tab for examples.
            </p>
          </div>
        </div>
      )}

      {/* REST API Tab */}
      {activeTab === 'rest' && (
        <div className="space-y-6">
          <div className="bg-card rounded-xl border p-4">
            <h3 className="mb-2 font-medium">API Base URL</h3>
            <div className="flex items-center gap-2">
              <code className="bg-muted flex-1 rounded-lg px-3 py-2 font-mono text-sm">
                {apiUrl}
              </code>
              <button
                onClick={() => handleCopy(apiUrl, 'api-url')}
                className="hover:bg-muted rounded-lg p-2 transition-colors"
              >
                {copiedItems['api-url'] ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">
              Public Endpoints (require X-API-Key header)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 text-left font-medium">Method</th>
                    <th className="py-2 text-left font-medium">Endpoint</th>
                    <th className="py-2 text-left font-medium">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="py-2">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        POST
                      </span>
                    </td>
                    <td className="py-2 font-mono text-xs">/public/newsletter/subscribe</td>
                    <td className="text-muted-foreground py-2">Subscribe to newsletter</td>
                  </tr>
                  <tr>
                    <td className="py-2">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        POST
                      </span>
                    </td>
                    <td className="py-2 font-mono text-xs">/public/contact</td>
                    <td className="text-muted-foreground py-2">Submit contact form</td>
                  </tr>
                  <tr>
                    <td className="py-2">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        POST
                      </span>
                    </td>
                    <td className="py-2 font-mono text-xs">/public/consent</td>
                    <td className="text-muted-foreground py-2">Record consent</td>
                  </tr>
                  <tr>
                    <td className="py-2">
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        GET
                      </span>
                    </td>
                    <td className="py-2 font-mono text-xs">/public/consent/settings</td>
                    <td className="text-muted-foreground py-2">Get consent settings</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Terminal className="h-4 w-4" />
              cURL Examples
            </h3>
            <CodeBlock code={curlExamples} language="bash" copyKey="curl" />
          </div>
        </div>
      )}

      {/* Examples Tab */}
      {activeTab === 'examples' && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-medium">Newsletter Subscription</h3>
            <CodeBlock
              code={newsletterExample}
              language="typescript"
              copyKey="newsletter-example"
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Contact Form</h3>
            <CodeBlock code={contactExample} language="typescript" copyKey="contact-example" />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">React Newsletter Component</h3>
            <CodeBlock code={reactFormExample} language="tsx" copyKey="react-form" />
          </div>
        </div>
      )}

      {/* Documentation Link */}
      <div className="border-t pt-6">
        <a href="#" className="text-primary inline-flex items-center gap-2 text-sm hover:underline">
          <Book className="h-4 w-4" />
          View full API documentation
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
